import { Modal, Form } from "react-bootstrap";
import "./StoreModal.css";
import { useShoppingCart } from "../../context/ShoppingCartContext";
import { useState, useEffect, useCallback } from "react";
import RadioSkeleton from "../skeletons/RadioSkeleton";
import { formatCurrency } from "../../utilities/formatCurrency";
import { motion, AnimatePresence } from "framer-motion";
import {
  fetchProductOptionList,
  fetchProductOptions,
} from "../../lib/webService/webService";
import { X, Plus, ArrowClockwise, ExclamationCircleFill } from "react-bootstrap-icons";

const MAX_RETRIES = 3;

// ─── StoreModal ───────────────────────────────────────────────────────────────

const StoreModal = ({
  productId,
  name,
  description,
  category,
  price,
  imageSrc,
  showModal,
  handleClose,
  storeId,
  type,
  subType,
}) => {
  const { increaseCartQuantity } = useShoppingCart();
  const [validated, setValidated] = useState(false);
  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [sopsArray, setSopsArray] = useState([]);
  const [requiredOpIdsArr, setRequiredOpIdsArr] = useState([]);

  const fetchOptions = useCallback(async () => {
    try {
      setError(null);
      setIsLoading(true);
      const data = await fetchProductOptions(productId);
      if (!data) throw new Error("No options data received");
      setOptions(data);
      setRetryCount(0);
    } catch (err) {
      console.error("Error fetching options:", err);
      if (retryCount < MAX_RETRIES) {
        setRetryCount((prev) => prev + 1);
        setTimeout(() => fetchOptions(), 1000 * retryCount);
        setError(`Failed to load options. Retrying… (${retryCount + 1}/${MAX_RETRIES})`);
      } else {
        setError("Failed to load options. Please try again later.");
      }
    } finally {
      setIsLoading(false);
    }
  }, [productId, retryCount]);

  useEffect(() => {
    if (showModal) fetchOptions();
  }, [showModal, fetchOptions]);

  useEffect(() => {
    if (options.length > 0) {
      setSopsArray(options.map((option) => ({
        optionId: "", optionItemId: "", name: option.name, value: "",
      })));
      setRequiredOpIdsArr(options.filter((o) => o.required).map((o) => o.id));
    }
  }, [options]);

  const handleAddToCartAction = async (event) => {
    event.preventDefault();
    setValidated(true);
    const newArray = sopsArray.map(({ name, value, ...sop }) => sop);

    try {
      if (options.length > 0) {
        if (requiredOpIdsArr.length > 0) {
          const errArr = [];
          for (var m = 0; m < requiredOpIdsArr.length; m++) {
            let counter = 0;
            for (var n = 0; n < newArray.length; n++) {
              if (requiredOpIdsArr[m] !== newArray[n].optionId) counter++;
            }
            if (counter === newArray.length) errArr.push(requiredOpIdsArr[m]);
          }
          if (errArr.length === 0) {
            await increaseCartQuantity(productId, storeId, newArray);
            setTimeout(() => handleClose(), 1500);
          }
        }
      } else {
        await increaseCartQuantity(productId, storeId);
        setTimeout(() => handleClose(), 1500);
      }
    } catch (err) {
      console.error("Error adding to cart:", err);
      setError("Failed to add item to cart. Please try again.");
    }
  };

  const handleRetry = useCallback(() => {
    setRetryCount(0);
    fetchOptions();
  }, [fetchOptions]);

  return (
    <>
      <style>{`
        .store-modal .modal-content {
          border-radius: 20px !important;
          border: none !important;
          overflow: hidden;
          box-shadow: 0 12px 48px rgba(0,0,0,0.16) !important;
        }
        .store-modal .modal-dialog { max-width: 500px; }
        .store-modal .modal-content > form { display: contents; }
        .store-modal .modal-header { border-bottom: 1px solid rgba(60,60,67,0.08) !important; }
        .store-modal .modal-footer { border-top: 1px solid rgba(60,60,67,0.08) !important; }
        @media (max-width: 576px) {
          .store-modal .modal-dialog { margin: 12px; max-width: 100%; }
        }
        @media (max-width: 767.98px) {
          .store-modal .modal-footer {
            padding-bottom: calc(4.75rem + env(safe-area-inset-bottom, 0px)) !important;
          }
        }
      `}</style>

      <Modal
        show={showModal}
        onHide={handleClose}
        centered
        scrollable
        dialogClassName="store-modal"
      >
        <Form noValidate validated={validated} onSubmit={handleAddToCartAction}>

          {/* ── Header ────────────────────────────────────────────── */}
          <Modal.Header style={{ padding: "14px 20px" }}>
            <h2 style={{
              fontSize: "1.0625rem", fontWeight: 700, color: "#1c1c1e",
              letterSpacing: "-0.01em", margin: 0,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              flex: 1, paddingRight: 10,
            }}>
              {name}
            </h2>
            <button
              type="button"
              onClick={handleClose}
              style={{
                width: 30, height: 30, borderRadius: "50%",
                backgroundColor: "rgba(60,60,67,0.1)", border: "none",
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer", flexShrink: 0,
              }}
            >
              <X size={15} color="#3c3c43" />
            </button>
          </Modal.Header>

          {/* ── Body (scrolls) ────────────────────────────────────── */}
          <Modal.Body style={{ padding: "20px" }}>
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.22 }}
            >

              {/* Error banner */}
              <AnimatePresence>
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    style={{
                      backgroundColor: "rgba(255,59,48,0.08)",
                      borderRadius: 12, padding: "10px 14px",
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      marginBottom: 16,
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                      <ExclamationCircleFill size={14} color="#ff3b30" />
                      <span style={{ fontSize: "0.8125rem", color: "#ff3b30", fontWeight: 500 }}>
                        {error}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={handleRetry}
                      disabled={retryCount >= MAX_RETRIES}
                      style={{
                        background: "none", border: "none", cursor: "pointer",
                        color: "#ff3b30", fontSize: "0.8125rem", fontWeight: 600,
                        display: "flex", alignItems: "center", gap: 4, padding: 0,
                        opacity: retryCount >= MAX_RETRIES ? 0.4 : 1,
                      }}
                    >
                      <ArrowClockwise size={13} /> Retry
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Image */}
              <div style={{ display: "flex", justifyContent: "center", marginBottom: 18 }}>
                <div style={{
                  width: 180, height: category === "liquor" ? 220 : 180,
                  borderRadius: 16, overflow: "hidden",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <img
                    src={imageSrc}
                    alt={name}
                    style={{ width: "100%", height: "100%", objectFit: "contain" }}
                    onError={(e) => {
                      e.target.src = "https://via.placeholder.com/300x200?text=No+Image";
                      e.target.onerror = null;
                    }}
                  />
                </div>
              </div>

              {/* Price */}
              <div style={{ textAlign: "center", marginBottom: 10 }}>
                <span style={{
                  fontSize: "1.375rem", fontWeight: 700, color: "#007aff",
                  letterSpacing: "-0.02em",
                }}>
                  {formatCurrency(price)}
                </span>
              </div>

              {/* Description */}
              {description && (
                <p style={{
                  fontSize: "0.875rem", color: "rgba(60,60,67,0.6)",
                  lineHeight: 1.5, textAlign: "center",
                  margin: "0 0 20px",
                }}>
                  {description}
                </p>
              )}

              {/* Options */}
              {options.length > 0 && (
                <div style={{
                  borderTop: "1px solid rgba(60,60,67,0.08)",
                  paddingTop: 20,
                  display: "flex", flexDirection: "column", gap: 18,
                }}>
                  {options.map((option) => (
                    <RadioForms
                      key={option.id}
                      productId={productId}
                      option={option}
                      sopsArray={sopsArray}
                      isLoading={isLoading}
                      validated={validated}
                    />
                  ))}
                </div>
              )}

            </motion.div>
          </Modal.Body>

          {/* ── Footer (sticky) ───────────────────────────────────── */}
          <Modal.Footer style={{
            padding: "12px 20px",
            paddingBottom: "calc(12px + env(safe-area-inset-bottom, 0px))",
          }}>
            <div style={{ display: "flex", gap: 10, width: "100%" }}>
              <button
                type="button"
                onClick={handleClose}
                style={{
                  flex: 1, padding: "13px", borderRadius: 12, border: "none",
                  backgroundColor: "rgba(60,60,67,0.08)", color: "#1c1c1e",
                  fontSize: "0.9375rem", fontWeight: 600, cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <motion.button
                type="submit"
                disabled={isLoading}
                whileTap={{ scale: 0.97 }}
                style={{
                  flex: 2, padding: "13px", borderRadius: 12, border: "none",
                  backgroundColor: isLoading ? "rgba(0,122,255,0.5)" : "#007aff",
                  color: "#fff", fontSize: "0.9375rem", fontWeight: 600,
                  cursor: isLoading ? "not-allowed" : "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                }}
              >
                <Plus size={18} /> Add to Cart
              </motion.button>
            </div>
          </Modal.Footer>

        </Form>
      </Modal>
    </>
  );
};

// ─── RadioForms ───────────────────────────────────────────────────────────────

const RadioForms = ({ productId, option, sopsArray, isLoading, validated }) => {
  const [list, setList] = useState([]);
  const [isListLoading, setIsListLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [selectedItemId, setSelectedItemId] = useState(null);

  const fetchOptionList = useCallback(async () => {
    try {
      setError(null);
      setIsListLoading(true);
      const data = await fetchProductOptionList(productId, option.id);
      if (!data) throw new Error("No option list data received");
      setList(data);
      setRetryCount(0);
    } catch (err) {
      console.error("Error fetching option list:", err);
      if (retryCount < MAX_RETRIES) {
        setRetryCount((prev) => prev + 1);
        setTimeout(() => fetchOptionList(), 1000 * retryCount);
        setError(`Failed to load options. Retrying… (${retryCount + 1}/${MAX_RETRIES})`);
      } else {
        setError("Failed to load options. Please try again later.");
      }
    } finally {
      setIsListLoading(false);
    }
  }, [productId, option.id, retryCount]);

  useEffect(() => { fetchOptionList(); }, [fetchOptionList]);

  const handleRadioChange = (event, name, opId, opItemId, itemId) => {
    setSelectedItemId(itemId);
    const newSopArr = [];
    for (var i = 0; i < sopsArray.length; i++) {
      let sop = sopsArray[i];
      if (sop.name === name) {
        sop.optionId = opId;
        sop.optionItemId = opItemId;
        sop.value = event.target.value;
      }
      newSopArr.push(sop);
    }
    sopsArray = [...newSopArr];
  };

  const showValidationError = validated && option.required && !selectedItemId;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22 }}
    >
      {/* Section header */}
      <div style={{
        display: "flex", alignItems: "center", gap: 8, marginBottom: 10,
      }}>
        <span style={{
          fontSize: "0.875rem", fontWeight: 600,
          color: "#1c1c1e", letterSpacing: "-0.005em",
        }}>
          {option.title}
        </span>
        {option.required ? (
          <span style={{
            fontSize: "0.6875rem", fontWeight: 600,
            color: "#007aff", backgroundColor: "rgba(0,122,255,0.1)",
            borderRadius: 999, padding: "2px 8px",
          }}>
            Required
          </span>
        ) : (
          <span style={{
            fontSize: "0.6875rem", fontWeight: 500,
            color: "rgba(60,60,67,0.45)", backgroundColor: "rgba(118,118,128,0.1)",
            borderRadius: 999, padding: "2px 8px",
          }}>
            Optional
          </span>
        )}
      </div>

      {/* Options list card */}
      <Form.Group controlId={option.id}>
        <div style={{
          backgroundColor: "#fff",
          border: showValidationError
            ? "1px solid rgba(255,59,48,0.5)"
            : "1px solid rgba(15,23,42,0.07)",
          borderRadius: 14,
          overflow: "hidden",
          boxShadow: "0 1px 4px rgba(15,23,42,0.04)",
        }}>
          <AnimatePresence mode="wait">
            {isListLoading ? (
              <div style={{ padding: "12px 16px" }}>
                <RadioSkeleton />
              </div>
            ) : error ? (
              <div style={{
                padding: "12px 16px", fontSize: "0.8125rem",
                color: "#ff3b30", display: "flex", alignItems: "center", gap: 6,
              }}>
                <ExclamationCircleFill size={13} color="#ff3b30" />
                {error}
              </div>
            ) : (
              list.map((item, i) => {
                const isSelected = selectedItemId === item.id;
                return (
                  <motion.label
                    key={`radio-${item.id}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: i * 0.04 }}
                    htmlFor={`radio-${item.id}`}
                    style={{
                      display: "flex", alignItems: "center",
                      justifyContent: "space-between",
                      padding: "13px 16px",
                      cursor: "pointer",
                      backgroundColor: isSelected ? "rgba(0,122,255,0.05)" : "transparent",
                      borderTop: i > 0 ? "1px solid rgba(60,60,67,0.06)" : "none",
                      transition: "background-color 0.15s ease",
                      userSelect: "none",
                    }}
                  >
                    <span style={{
                      fontSize: "0.9375rem",
                      color: isSelected ? "#007aff" : "#1c1c1e",
                      fontWeight: isSelected ? 500 : 400,
                    }}>
                      {item.value}
                    </span>

                    {/* Custom radio circle */}
                    <div style={{
                      width: 22, height: 22, borderRadius: "50%", flexShrink: 0,
                      border: `2px solid ${isSelected ? "#007aff" : "rgba(60,60,67,0.2)"}`,
                      backgroundColor: isSelected ? "#007aff" : "transparent",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      transition: "all 0.15s ease",
                    }}>
                      {isSelected && (
                        <div style={{
                          width: 8, height: 8, borderRadius: "50%", backgroundColor: "#fff",
                        }} />
                      )}
                    </div>

                    {/* Hidden native radio for form accessibility */}
                    <input
                      type="radio"
                      id={`radio-${item.id}`}
                      required={option.required}
                      value={item.id}
                      name={option.name}
                      checked={isSelected}
                      onChange={(e) => handleRadioChange(e, option.name, option.id, item.id, item.id)}
                      style={{ position: "absolute", opacity: 0, pointerEvents: "none" }}
                    />
                  </motion.label>
                );
              })
            )}
          </AnimatePresence>
        </div>

        {showValidationError && (
          <div style={{
            fontSize: "0.78rem", color: "#ff3b30",
            display: "flex", alignItems: "center", gap: 5,
            marginTop: 6, paddingLeft: 4,
          }}>
            <ExclamationCircleFill size={11} color="#ff3b30" />
            Please select an option
          </div>
        )}
      </Form.Group>
    </motion.div>
  );
};

export default StoreModal;
